import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './blocks.module.css';

const VIEWPORT_GAP = 8;
const ANCHOR_GAP = 8;
const PANEL_MAX_WIDTH = 352; // 22rem

function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
}

function computePanelStyle(anchorRect, panelRect) {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const width = Math.min(PANEL_MAX_WIDTH, Math.max(160, vw - VIEWPORT_GAP * 2));
    const height = panelRect?.height || 0;

    let left = anchorRect.left;
    left = clamp(left, VIEWPORT_GAP, Math.max(VIEWPORT_GAP, vw - width - VIEWPORT_GAP));

    const spaceBelow = vh - anchorRect.bottom - ANCHOR_GAP - VIEWPORT_GAP;
    const spaceAbove = anchorRect.top - ANCHOR_GAP - VIEWPORT_GAP;
    const placeAbove = height > 0
        && height > spaceBelow
        && spaceAbove > spaceBelow;

    let top;
    if (placeAbove) {
        top = anchorRect.top - ANCHOR_GAP - height;
    } else {
        top = anchorRect.bottom + ANCHOR_GAP;
    }
    top = clamp(top, VIEWPORT_GAP, Math.max(VIEWPORT_GAP, vh - Math.max(height, 1) - VIEWPORT_GAP));

    return {
        position: 'fixed',
        top,
        left,
        width,
        maxHeight: vh - VIEWPORT_GAP * 2,
        zIndex: 400,
    };
}

/**
 * Compact ? control that opens a popover. Click again, click away, or Escape to close.
 * Panel is portaled to document.body so overflow:hidden ancestors cannot clip it.
 */
export default function HelpPopover({ label, title, children }) {
    const [open, setOpen] = useState(false);
    const [panelStyle, setPanelStyle] = useState(null);
    const rootRef = useRef(null);
    const buttonRef = useRef(null);
    const panelRef = useRef(null);
    const panelId = useId();

    useLayoutEffect(() => {
        if (!open) {
            setPanelStyle(null);
            return undefined;
        }

        const updatePosition = () => {
            const button = buttonRef.current;
            if (!button) return;
            const anchorRect = button.getBoundingClientRect();
            const panelRect = panelRef.current?.getBoundingClientRect();
            setPanelStyle(computePanelStyle(anchorRect, panelRect));
        };

        updatePosition();
        // Second pass after paint so height/width are accurate for flip + clamp.
        const raf = window.requestAnimationFrame(updatePosition);

        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);
        return () => {
            window.cancelAnimationFrame(raf);
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [open, children, title]);

    useEffect(() => {
        if (!open) return undefined;

        const onPointerDown = (event) => {
            const target = event.target;
            if (rootRef.current?.contains(target)) return;
            if (panelRef.current?.contains(target)) return;
            setOpen(false);
        };
        const onKeyDown = (event) => {
            if (event.key === 'Escape') {
                setOpen(false);
            }
        };

        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [open]);

    const panel = open && typeof document !== 'undefined'
        ? createPortal(
            <div
                id={panelId}
                ref={panelRef}
                className={`${styles.helpPanel} ${styles.helpPanelPortal}`}
                role="dialog"
                aria-label={title || label || 'Help'}
                style={panelStyle || { position: 'fixed', top: 0, left: 0, visibility: 'hidden' }}
            >
                {title && <h4 className={styles.helpPanelTitle}>{title}</h4>}
                {children}
            </div>,
            document.body,
        )
        : null;

    return (
        <span className={styles.helpPopover} ref={rootRef}>
            <button
                ref={buttonRef}
                type="button"
                className={styles.helpButton}
                aria-label={label || title || 'Help'}
                aria-expanded={open}
                aria-controls={panelId}
                onClick={() => setOpen((value) => !value)}
            >
                ?
            </button>
            {panel}
        </span>
    );
}
